import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import { Switch } from "antd";
import {
  ArrowLeftOutlined, UserOutlined, MailOutlined, LockOutlined,
  SafetyCertificateOutlined, PhoneOutlined, NumberOutlined,
  EyeOutlined, EyeInvisibleOutlined, CheckOutlined, TeamOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { authHeaders } from "../utils/session";

const authH = () => ({ ...authHeaders(), "Content-Type": "application/json" });

const inputStyle = (err, focus) => ({
  width: "100%", height: "44px", padding: "0 14px", borderRadius: "10px",
  border: `1.5px solid ${err ? "#ef4444" : "#e5e7eb"}`, fontSize: "14px",
  outline: "none", color: "#111827", boxSizing: "border-box",
  boxShadow: err ? "0 0 0 3px rgba(239,68,68,0.1)" : "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
});
const viewInputStyle = (extra = {}) => ({ background: "#f9fafb", cursor: "default", color: "#374151", ...extra });

const Field = ({ label, required, hint, error, children, style: wrapStyle }) => (
  <div style={{ marginBottom: "18px", ...wrapStyle }}>
    <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "6px" }}>
      {label}
      {required && <span style={{ color: "#ef4444" }}> *</span>}
      {hint && <span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af", marginLeft: "4px" }}>{hint}</span>}
    </label>
    {children}
    {error && <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>{error}</div>}
  </div>
);

const EMPTY_FORM = {
  name: "", email: "", phone: "", password: "", confirm_password: "",
  cms_role_id: "", reporting_manager_id: "",
  lead_limit: "", invoice_limit: "", status: 1,
};

const CreateEditTeamMember = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!editId;
  const isView = searchParams.get("view") === "1";

  const [saving, setSaving] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [roles, setRoles] = useState([]);
  /* Only the roles this user may hand out — strictly below their own. The full
     cms-roles list is still fetched, but purely to resolve a role's slug for the
     payload and to name a role the picker does not offer. */
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [reportingMembers, setReportingMembers] = useState([]);
  const [currentManagerName, setCurrentManagerName] = useState("");
  const [loadingManagers, setLoadingManagers] = useState(false);

  // Load
  useEffect(() => {
    const load = async () => {
      setPageLoading(true);
      try {
        if (isEdit) {
          const [rolesRes, memberRes, assignableRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/cms-roles`, { headers: authH() }),
            axios.get(`${API_BASE_URL}/get-internal-user-details/${editId}`, { headers: authH() }),
            axios.get(`${API_BASE_URL}/assignable-roles`, { headers: authH() }),
          ]);
          setRoles(rolesRes.data.data || []);
          setAssignableRoles(assignableRes.data?.data || []);
          const member = memberRes.data?.data;
          if (member) {
            setForm({
              name: member.name || "",
              email: member.email || "",
              phone: member.mobile || "",
              password: "", confirm_password: "",
              cms_role_id: member.cms_role_id ?? "",
              reporting_manager_id: member.reporting_to_user_id ?? "",
              lead_limit: member.lead_limit ?? "", invoice_limit: member.invoice_limit ?? "",
              status: member.status ?? 1,
            });
            // Shown if the stored manager is not among this role's seniors.
            setCurrentManagerName(member.reporting_to_user_name || "");
            // The dropdown follows the role, so it is filled from the saved one.
            if (member.cms_role_id) await loadReportingMembers(member.cms_role_id);
          } else { toast.error("Member not found"); navigate("/cms/manage-team-members"); }
        } else {
          const [rolesRes, assignableRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/cms-roles`, { headers: authH() }),
            axios.get(`${API_BASE_URL}/assignable-roles`, { headers: authH() }),
          ]);
          setRoles(rolesRes.data.data || []);
          setAssignableRoles(assignableRes.data?.data || []);
        }
      } catch { toast.error("Failed to load data"); }
      finally { setPageLoading(false); }
    };
    load();
  }, [editId, isEdit, navigate]);

  /* Who this member may report to is a property of their ROLE: the API returns
     the senior roles for it and the people holding them. It is not the viewer's
     own subtree, and it is never the flat user list — that showed everyone. */
  const managerOptions = (() => {
    const options = reportingMembers.filter(
      // Nobody reports to themselves.
      (m) => !(isEdit && String(m.id) === String(editId)),
    );
    /* An existing manager can fall outside the current role's seniors (their
       role changed, say). Keeping it stops the field from blanking and quietly
       dropping their manager on save. */
    if (
      form.reporting_manager_id &&
      !options.some((o) => String(o.id) === String(form.reporting_manager_id))
    ) {
      options.push({
        id: form.reporting_manager_id,
        name: currentManagerName || `Current manager (#${form.reporting_manager_id})`,
      });
    }
    return options;
  })();

  /* On edit the member may already hold a role at or above the viewer's — it is
     kept in the list so the field does not blank out and silently change their
     role on save. */
  const roleOptions = (() => {
    const options = [...assignableRoles];
    const current = form.cms_role_id;
    if (current && !options.some((r) => String(r.id) === String(current))) {
      const known = roles.find((r) => String(r.id) === String(current));
      options.push({ id: current, role_name: known?.role_name || `Current role (#${current})` });
    }
    return options;
  })();

  /** Seniors for a role, flattened out of the API's per-role groups. */
  const loadReportingMembers = async (roleId) => {
    if (!roleId) { setReportingMembers([]); return; }
    setLoadingManagers(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/cms-roles/${roleId}/reporting-members`, { headers: authH() });
      const groups = res.data?.data || [];
      setReportingMembers(
        groups.flatMap((g) =>
          (g.members || []).map((m) => ({ ...m, roleName: g.role_name }))),
      );
    } catch { setReportingMembers([]); }
    finally { setLoadingManagers(false); }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = "Invalid email format";
    if (form.phone && form.phone.length > 0 && form.phone.length < 10) errs.phone = "Phone must be 10 digits";
    if (!isEdit && !form.password.trim()) errs.password = "Password is required";
    if (form.password && form.password.length < 8) errs.password = "Min 8 characters";
    if (form.password && form.password !== form.confirm_password) errs.confirm_password = "Passwords do not match";
    if (!isEdit && form.password && !form.confirm_password) errs.confirm_password = "Confirm your password";
    if (!form.cms_role_id) errs.cms_role_id = "Please select a role";
    setErrors(errs);
    if (Object.keys(errs).length > 0) window.scrollTo({ top: 0, behavior: "smooth" });
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      name: form.name.trim(), 
      email: form.email.trim(), 
      mobile: form.phone.trim() || null,
      role: roles.find(r => r.id === Number(form.cms_role_id))?.slug || "member",
      cms_role_id: Number(form.cms_role_id),
      reporting_to_user_id: form.reporting_manager_id ? Number(form.reporting_manager_id) : null,
      lead_limit: form.lead_limit !== "" ? Number(form.lead_limit) : 0,
      invoice_limit: form.invoice_limit !== "" ? Number(form.invoice_limit) : 0,
    //   status: form.status,
    };
    if (form.password) payload.password = form.password;

    setSaving(true);
    try {
      const res = isEdit
        ? await axios.post(`${API_BASE_URL}/update-internal-user-details/${editId}`, payload, { headers: authH() })
        : await axios.post(`${API_BASE_URL}/add-team-member`, payload, { headers: authH() });
      const data = res?.data;
      if (data?.status === 422 || data?.errors) {
        const apiErrs = {};
        if (data.errors?.email) apiErrs.email = data.errors.email[0];
        if (data.errors?.mobile) apiErrs.phone = data.errors.mobile[0];
        setErrors(p => ({ ...p, ...apiErrs }));
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.success(isEdit ? "Member updated!" : "Member added!");
        navigate("/cms/manage-team-members");
      }
    } catch (err) {
      const data = err?.response?.data;
      /* The API scopes by reporting line: saving someone outside this user's
         subtree is refused. The list only shows their own people, so this is a
         safety net rather than an everyday path. */
      if (err?.response?.status === 403) {
        toast.error(
          data?.message ||
            "You can only assign roles below your own, and manage employees who report to you.",
        );
      } else if (data?.errors) {
        const apiErrs = {};
        if (data.errors.email) apiErrs.email = data.errors.email[0];
        if (data.errors.mobile) apiErrs.phone = data.errors.mobile[0];
        setErrors(p => ({ ...p, ...apiErrs }));
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(data?.message || "Failed to save");
      }
    } finally { setSaving(false); }
  };

  const getRoleName = (id) => roles.find(r => r.id === id)?.role_name || "";

  const selectStyle = (err) => ({
    ...inputStyle(err), cursor: "pointer", appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M2 4l4 4 4-4z'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center",
  });

  if (pageLoading) {
    return (
      <div className="main-content"><div className="page-content"><div className="container-fluid">
        <div style={{ padding: "60px", textAlign: "center" }}>
          <div className="spinner-border" style={{ color: "#0f172a", width: "40px", height: "40px" }} role="status" />
          <div style={{ fontSize: "14px", color: "#9ca3af", marginTop: "16px" }}>Loading...</div>
        </div>
      </div></div></div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Top Bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
            <div>
              <h4 style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: "#111827" }}>{isView ? (form.name || "Team Member") : isEdit ? "Edit Team Member" : "Add Team Member"}</h4>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#9ca3af" }}>{isView ? `Member ID: ${editId}` : isEdit ? `Editing member ID: ${editId}` : "Add a new CMS internal user"}</p>
            </div>
            <Link to="/cms/manage-team-members" style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 18px", borderRadius: "10px", border: "1px solid #e5e7eb", background: "white", color: "#374151", fontWeight: 600, fontSize: "13px", textDecoration: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <ArrowLeftOutlined /> Back
            </Link>
          </div>

          {/* Main Content — Two Column */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: "28px", alignItems: "start" }}>

          {/* LEFT: Form */}
          <div>

            {/* Step 1: Role & Manager */}
            <div style={{ background: "white", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", overflow: "hidden", marginBottom: "20px" }}>
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "10px", background: "#fafbfc" }}>
                <SafetyCertificateOutlined style={{ color: "#5b48e8", fontSize: "16px" }} />
                <span style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>Role Assignment</span>
              </div>
              <div style={{ padding: "24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <span style={{ width: "26px", height: "26px", borderRadius: "50%", background: "#5b48e8", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700 }}>1</span>
                      <span style={{ fontSize: "14px", fontWeight: 700, color: "#374151" }}>Role <span style={{ color: "#ef4444" }}>*</span></span>
                    </div>
                    <select value={form.cms_role_id} onChange={e => { if (isView) return; const roleId = e.target.value; setCurrentManagerName(""); setForm(p => ({ ...p, cms_role_id: roleId, reporting_manager_id: "" })); if (roleId) setErrors(p => ({ ...p, cms_role_id: "" })); loadReportingMembers(roleId); }}
                      disabled={isView} style={{ ...selectStyle(errors.cms_role_id), ...(isView ? viewInputStyle() : {}) }}>
                      <option value="">-- Select Role --</option>
                      {roleOptions.map(r => <option key={r.id} value={r.id}>{r.role_name}</option>)}
                    </select>
                    {errors.cms_role_id && <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>{errors.cms_role_id}</div>}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <span style={{ width: "26px", height: "26px", borderRadius: "50%", background: "#2563eb", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700 }}>2</span>
                      <span style={{ fontSize: "14px", fontWeight: 700, color: "#374151" }}>Reporting Manager</span>
                    </div>
                    <select value={form.reporting_manager_id} onChange={e => { if (isView) return; setForm(p => ({ ...p, reporting_manager_id: e.target.value })); }}
                      disabled={isView || !form.cms_role_id || loadingManagers || managerOptions.length === 0}
                      style={{ ...selectStyle(false), ...(isView ? viewInputStyle() : { opacity: !form.cms_role_id ? 0.5 : 1, cursor: form.cms_role_id && !loadingManagers && managerOptions.length > 0 ? "pointer" : "not-allowed" }) }}>
                      <option value="">
                        {!form.cms_role_id ? "Select role first"
                          : loadingManagers ? "Loading managers…"
                          : managerOptions.length === 0 ? "No reporting manager available for this role"
                          : "Select manager..."}
                      </option>
                      {managerOptions.map(m => <option key={m.id} value={m.id}>{m.roleName ? `${m.name} — ${m.roleName}` : m.name}</option>)}
                    </select>
                  </div>
                </div>

              </div>
            </div>

            {/* Member Information */}
            <div style={{ background: "white", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", overflow: "hidden", marginBottom: "20px" }}>
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "10px", background: "#fafbfc" }}>
                <UserOutlined style={{ color: "#0891b2", fontSize: "16px" }} />
                <span style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>Member Information</span>
              </div>
              <div style={{ padding: "24px" }}>
                {/* Name + Mobile */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <Field label="Full Name" required error={errors.name}>
                    <div style={{ position: "relative" }}>
                      <UserOutlined style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                      <input value={form.name} onChange={e => { if (isView) return; setForm(p => ({ ...p, name: e.target.value })); if (e.target.value.trim()) setErrors(p => ({ ...p, name: "" })); }}
                        placeholder="e.g. Rahul Sharma" style={{ ...inputStyle(errors.name), paddingLeft: "40px", ...(isView ? viewInputStyle() : {}) }}
                        onFocus={e => { if (!isView && !errors.name) { e.target.style.borderColor = "#5b48e8"; e.target.style.boxShadow = "0 0 0 3px rgba(91,72,232,0.1)"; } }}
                        onBlur={e => { if (!isView && !errors.name) { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; } }} readOnly={isView} />
                    </div>
                  </Field>
                  <Field label="Mobile Number" error={errors.phone}>
                    <div style={{ position: "relative" }}>
                      <PhoneOutlined style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                      <input value={form.phone} onChange={e => { if (isView) return; const v = e.target.value.replace(/\D/g, "").slice(0, 10); setForm(p => ({ ...p, phone: v })); }}
                        placeholder="e.g. 9876543210" maxLength={10} style={{ ...inputStyle(errors.phone), paddingLeft: "40px", ...(isView ? viewInputStyle() : {}) }}
                        onFocus={e => { if (!isView && !errors.phone) { e.target.style.borderColor = "#5b48e8"; e.target.style.boxShadow = "0 0 0 3px rgba(91,72,232,0.1)"; } }}
                        onBlur={e => { if (!isView && !errors.phone) { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; } }} readOnly={isView} />
                    </div>
                  </Field>
                </div>

                {/* Email */}
                <Field label="Email Address" required error={errors.email}>
                  <div style={{ position: "relative" }}>
                    <MailOutlined style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                    <input type="email" value={form.email} onChange={e => { if (isView) return; setForm(p => ({ ...p, email: e.target.value })); if (e.target.value.trim()) setErrors(p => ({ ...p, email: "" })); }}
                      placeholder="member@example.com" autoComplete="off" style={{ ...inputStyle(errors.email), paddingLeft: "40px", ...(isView ? viewInputStyle() : {}) }}
                      onFocus={e => { if (!isView && !errors.email) { e.target.style.borderColor = "#5b48e8"; e.target.style.boxShadow = "0 0 0 3px rgba(91,72,232,0.1)"; } }}
                      onBlur={e => { if (!isView && !errors.email) { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; } }} readOnly={isView} />
                  </div>
                </Field>

                {/* Password + Confirm */}
                {!isView && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <Field label="Password" required={!isEdit} hint={isEdit ? "(leave blank to keep current)" : ""} error={errors.password}>
                    <div style={{ position: "relative" }}>
                      <LockOutlined style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                      <input type={showPassword ? "text" : "password"} value={form.password}
                        onChange={e => { setForm(p => ({ ...p, password: e.target.value })); setErrors(p => ({ ...p, password: "" })); }}
                        placeholder="Min. 8 characters" autoComplete="new-password"
                        style={{ ...inputStyle(errors.password), paddingLeft: "40px", paddingRight: "44px" }}
                        onFocus={e => { if (!errors.password) { e.target.style.borderColor = "#5b48e8"; e.target.style.boxShadow = "0 0 0 3px rgba(91,72,232,0.1)"; } }}
                        onBlur={e => { if (!errors.password) { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; } }} />
                      <button type="button" onClick={() => setShowPassword(p => !p)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "16px", padding: 0 }}>
                        {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      </button>
                    </div>
                  </Field>
                  <Field label="Confirm Password" required={!isEdit && !!form.password} error={errors.confirm_password}>
                    <div style={{ position: "relative" }}>
                      <LockOutlined style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                      <input type={showConfirm ? "text" : "password"} value={form.confirm_password}
                        onChange={e => { const v = e.target.value; setForm(p => ({ ...p, confirm_password: v })); setErrors(p => ({ ...p, confirm_password: v && form.password && v !== form.password ? "Passwords do not match" : "" })); }}
                        placeholder="Repeat password" autoComplete="new-password"
                        style={{ ...inputStyle(errors.confirm_password), paddingLeft: "40px", paddingRight: "44px" }}
                        onFocus={e => { if (!errors.confirm_password) { e.target.style.borderColor = "#5b48e8"; e.target.style.boxShadow = "0 0 0 3px rgba(91,72,232,0.1)"; } }}
                        onBlur={e => { if (!errors.confirm_password) { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; } }} />
                      <button type="button" onClick={() => setShowConfirm(p => !p)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "16px", padding: 0 }}>
                        {showConfirm ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      </button>
                    </div>
                  </Field>
                </div>}
              </div>
            </div>

            {/* Limits & Status */}
            <div style={{ background: "white", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", overflow: "hidden", marginBottom: "20px" }}>
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "10px", background: "#fafbfc" }}>
                <NumberOutlined style={{ color: "#d97706", fontSize: "16px" }} />
                <span style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>Limits & Status</span>
              </div>
              <div style={{ padding: "24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", alignItems: "end" }}>
                  <Field label="Lead Limit" hint="(0 = unlimited)">
                    <div style={{ position: "relative" }}>
                      <NumberOutlined style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                      <input type="number" min="0" value={form.lead_limit} onChange={e => { if (isView) return; setForm(p => ({ ...p, lead_limit: e.target.value })); }} placeholder="e.g. 50" style={{ ...inputStyle(false), paddingLeft: "40px", ...(isView ? viewInputStyle() : {}) }} readOnly={isView} />
                    </div>
                  </Field>
                  <Field label="Invoice Limit" hint="(0 = unlimited)">
                    <div style={{ position: "relative" }}>
                      <NumberOutlined style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                      <input type="number" min="0" value={form.invoice_limit} onChange={e => { if (isView) return; setForm(p => ({ ...p, invoice_limit: e.target.value })); }} placeholder="e.g. 30" style={{ ...inputStyle(false), paddingLeft: "40px", ...(isView ? viewInputStyle() : {}) }} readOnly={isView} />
                    </div>
                  </Field>
                  {isEdit && (
                    <Field label="Status">
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "44px" }}>
                        <Switch checked={form.status === 1} onChange={v => { if (isView) return; setForm(p => ({ ...p, status: v ? 1 : 0 })); }} disabled={isView} style={{ background: form.status === 1 ? "#16a34a" : "#d1d5db" }} />
                        <span style={{ fontSize: "14px", fontWeight: 600, color: form.status === 1 ? "#16a34a" : "#9ca3af" }}>{form.status === 1 ? "Active" : "Inactive"}</span>
                      </div>
                    </Field>
                  )}
                </div>
              </div>
            </div>

            {/* Edit Info */}
            {isEdit && (
              <div style={{ background: "#f9fafb", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "14px 20px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                <InfoCircleOutlined style={{ color: "#6b7280", fontSize: "15px" }} />
                <div style={{ fontSize: "13px", color: "#6b7280", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  <span><strong>ID:</strong> {editId}</span>
                  <span><strong>Role:</strong> {getRoleName(Number(form.cms_role_id)) || "—"}</span>
                </div>
              </div>
            )}

          </div>

            {/* RIGHT: Illustration + Info Panel */}
            <div style={{ position: "sticky", top: "80px", display: "flex", flexDirection: "column", gap: "20px" }}>

              {/* SVG Illustration Card */}
              <div style={{ background: "linear-gradient(135deg,#f8f7ff 0%,#eef2ff 50%,#f0fdf4 100%)", borderRadius: "16px", border: "1px solid #e8ecff", padding: "32px 24px", textAlign: "center", overflow: "hidden", position: "relative" }}>
                {/* Decorative circles */}
                <div style={{ position: "absolute", top: "-20px", right: "-20px", width: "80px", height: "80px", borderRadius: "50%", background: "rgba(91,72,232,0.06)" }} />
                <div style={{ position: "absolute", bottom: "-15px", left: "-15px", width: "60px", height: "60px", borderRadius: "50%", background: "rgba(22,163,106,0.06)" }} />

                {/* Team SVG Illustration */}
                <svg width="200" height="160" viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: "0 auto 16px", display: "block" }}>
                  {/* Background shape */}
                  <ellipse cx="100" cy="140" rx="80" ry="12" fill="#e8ecff" opacity="0.6" />

                  {/* Center person (larger) */}
                  <circle cx="100" cy="52" r="22" fill="url(#grad1)" />
                  <circle cx="100" cy="42" r="14" fill="#fff" opacity="0.15" />
                  <circle cx="100" cy="38" r="8" fill="#fff" opacity="0.3" />
                  <path d="M72 105 C72 85 82 75 100 75 C118 75 128 85 128 105 L128 130 L72 130 Z" fill="url(#grad1)" rx="8" />
                  <rect x="80" y="105" width="40" height="3" rx="1.5" fill="#fff" opacity="0.2" />
                  <rect x="85" y="112" width="30" height="3" rx="1.5" fill="#fff" opacity="0.15" />

                  {/* Left person */}
                  <circle cx="42" cy="62" r="16" fill="#0891b2" opacity="0.8" />
                  <circle cx="42" cy="55" r="9" fill="#fff" opacity="0.2" />
                  <path d="M22 108 C22 92 30 84 42 84 C54 84 62 92 62 108 L62 130 L22 130 Z" fill="#0891b2" opacity="0.8" />

                  {/* Right person */}
                  <circle cx="158" cy="62" r="16" fill="#d97706" opacity="0.8" />
                  <circle cx="158" cy="55" r="9" fill="#fff" opacity="0.2" />
                  <path d="M138 108 C138 92 146 84 158 84 C170 84 178 92 178 108 L178 130 L138 130 Z" fill="#d97706" opacity="0.8" />

                  {/* Connection lines */}
                  <line x1="62" y1="95" x2="72" y2="90" stroke="#5b48e8" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.3" />
                  <line x1="138" y1="95" x2="128" y2="90" stroke="#5b48e8" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.3" />

                  {/* Badge/shield on center */}
                  <circle cx="100" cy="48" r="6" fill="#fff" opacity="0.9" />
                  <path d="M97 48 L99 50 L103 46" stroke="#5b48e8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />

                  {/* Sparkles */}
                  <circle cx="30" cy="40" r="2" fill="#5b48e8" opacity="0.2" />
                  <circle cx="170" cy="38" r="2.5" fill="#d97706" opacity="0.2" />
                  <circle cx="55" cy="30" r="1.5" fill="#0891b2" opacity="0.3" />
                  <circle cx="145" cy="28" r="1.5" fill="#16a34a" opacity="0.3" />
                  <circle cx="85" cy="22" r="2" fill="#5b48e8" opacity="0.15" />
                  <circle cx="115" cy="20" r="1.5" fill="#7c3aed" opacity="0.2" />

                  <defs>
                    <linearGradient id="grad1" x1="78" y1="30" x2="122" y2="130" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#5b48e8" />
                      <stop offset="1" stopColor="#7c3aed" />
                    </linearGradient>
                  </defs>
                </svg>

                <div style={{ fontSize: "16px", fontWeight: 700, color: "#1f2937", marginBottom: "4px" }}>
                  {isEdit ? "Update Team Member" : "Build Your Team"}
                </div>
                <div style={{ fontSize: "12px", color: "#9ca3af", lineHeight: 1.6, maxWidth: "260px", margin: "0 auto" }}>
                  {isEdit ? "Modify the member's role, permissions and reporting structure." : "Add team members, assign roles and define the reporting hierarchy for your CMS."}
                </div>
              </div>

              {/* Live Summary Card */}
              <div style={{ background: "white", borderRadius: "14px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", overflow: "hidden" }}>
                <div style={{ padding: "14px 18px", borderBottom: "1px solid #f0f0f0", background: "#fafbfc", display: "flex", alignItems: "center", gap: "8px" }}>
                  <InfoCircleOutlined style={{ color: "#5b48e8", fontSize: "14px" }} />
                  <span style={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>Summary</span>
                </div>
                <div style={{ padding: "14px 18px" }}>
                  {[
                    { label: "Name", value: form.name || "—", color: form.name ? "#111827" : "#d1d5db" },
                    { label: "Email", value: form.email || "—", color: form.email ? "#111827" : "#d1d5db" },
                    { label: "Phone", value: form.phone || "—", color: form.phone ? "#111827" : "#d1d5db" },
                    { label: "Role", value: getRoleName(Number(form.cms_role_id)) || "—", color: form.cms_role_id ? "#7c3aed" : "#d1d5db" },
                    { label: "Manager", value: form.reporting_manager_id ? (reportingMembers.find(m => m.id === Number(form.reporting_manager_id))?.name || "—") : "—", color: form.reporting_manager_id ? "#16a34a" : "#d1d5db" },
                  ].map(item => (
                    <div key={item.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #f9fafb" }}>
                      <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: 600 }}>{item.label}</span>
                      <span style={{ fontSize: "13px", color: item.color, fontWeight: 600, maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "right" }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Tips Card */}
              <div style={{ background: "#fffbeb", borderRadius: "14px", border: "1px solid #fde68a", padding: "16px 18px" }}>
                <div style={{ fontSize: "13px", fontWeight: 700, color: "#92400e", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                  💡 Quick Tips
                </div>
                <div style={{ fontSize: "12px", color: "#a16207", lineHeight: 1.7 }}>
                  <div style={{ marginBottom: "4px" }}>• Select a <strong>role first</strong> to load available managers</div>
                  <div style={{ marginBottom: "4px" }}>• Manager list is filtered by role hierarchy</div>
                  <div style={{ marginBottom: "4px" }}>• Password must be at least <strong>8 characters</strong></div>
                  <div>• Set limits to <strong>0</strong> for unlimited access</div>
                </div>
              </div>
            </div>
          </div>

            {/* Sticky Bottom */}
            {!isView && (
            <div style={{
              position: "sticky", bottom: 0, zIndex: 100,
              background: "white", borderTop: "2px solid #f0f0f0",
              padding: "16px 0", display: "flex", alignItems: "center",
              justifyContent: "flex-end", gap: "12px",
              boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
            }}>
              <Link to="/cms/manage-team-members" style={{ padding: "10px 24px", borderRadius: "10px", border: "1px solid #e5e7eb", background: "white", color: "#374151", fontWeight: 600, fontSize: "14px", textDecoration: "none" }}>Cancel</Link>
              <button onClick={handleSubmit} disabled={saving} style={{
                padding: "10px 32px", borderRadius: "10px", border: "none",
                background: saving ? "#94a3b8" : "linear-gradient(135deg,#0f172a,#334155)",
                color: "white", fontWeight: 700, fontSize: "14px",
                cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: "8px",
                boxShadow: "0 4px 14px rgba(15,23,42,0.35)",
              }}>
                {saving && <span className="spinner-border spinner-border-sm" role="status" />}
                {saving ? "Saving..." : (isEdit ? "Update Member" : "Add Member")}
              </button>
            </div>
            )}

        </div>
      </div>
    </div>
  );
};

export default CreateEditTeamMember;